<template>
    <div data-spec-class="table-wired">
        <div class="flex items-end justify-between px-6 pt-6 pb-3">
            <div>
                <slot name="actions" />
            </div>
            <div class="flex items-end space-x-6">
                <BaseFieldInput
                    v-if="searchableAttributeLabels && isSearchable"
                    :model-value="search"
                    :is-clearable="true"
                    :placeholder="searchableAttributeLabels"
                    :error-messages="validationError?.search"
                    name="search"
                    class="!w-64 py-0.5 focus:!w-80"
                    data-spec-class="table-wired__search"
                    autocomplete="new"
                    @update:model-value="(value) => emits('update:search', value)"
                    @clear="onSearchClear"
                    @keyup.enter="updatePage(1)"
                >
                    <template #addOn>
                        <div class="bg-surface pl-1">
                            <IconMagnifyingGlass
                                class="h-4 w-4 cursor-pointer fill-brand"
                                data-spec-class="table-wired__search_submit"
                                @click="updatePage(1)"
                            />
                        </div>
                    </template>
                </BaseFieldInput>
                <slot
                    v-if="isFilterable"
                    name="customFilters"
                />
            </div>
        </div>
        <BaseTable>
            <template #wrapper>
                <div class="relative">
                    <BaseOverlayLoading :is-loading="isLoading" />

                    <div class="md:rounded-lg">
                        <table class="min-w-full">
                            <BaseTableHead>
                                <BaseTableRow>
                                    <BaseTableCellHeader
                                        v-for="attribute in attributes"
                                        :key="attribute.position"
                                        :is-sortable="attribute.isSortable"
                                        :sorting="sorting"
                                        :attributeName="attribute.name"
                                        :sort-field="attribute.sortField"
                                        :class="attribute.columnClass"
                                        @update:page-sorting="onUpdatePageSorting"
                                    >
                                        {{ attribute.label }}
                                    </BaseTableCellHeader>
                                </BaseTableRow>
                            </BaseTableHead>
                            <tbody v-if="items.length">
                                <template
                                    v-for="(item, index) in items"
                                    :key="index"
                                >
                                    <slot
                                        name="item"
                                        v-bind="item"
                                    />
                                </template>
                            </tbody>
                            <tbody v-else>
                                <tr class="h-24">
                                    <td
                                        :colspan="attributes.length + 1"
                                        class="text-center text-tertiary"
                                        data-spec-class="table__empty"
                                    >
                                        {{ t('general.noData') }}
                                    </td>
                                </tr>
                            </tbody>
                            <tfoot v-if="meta">
                                <tr class="border-t border-divider">
                                    <td
                                        :colspan="attributes.length + 1"
                                        class="px-6 py-3"
                                    >
                                        <BasePagination
                                            :meta="meta"
                                            :has-meta-info="true"
                                            @update:page="(newPage) => updatePage(newPage)"
                                            @update:page-size="
                                                (newPageSize) => updatePage(1, newPageSize)
                                            "
                                        />
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </template>
        </BaseTable>
    </div>
</template>

<script lang="ts" setup>
import { withDefaults, defineEmits, defineProps, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
    Attribute,
    IndexValidationError,
    PageInformation,
    PaginationMeta,
    TableItem,
} from '@/types';

import BasePagination from './BasePagination.vue';
import BaseOverlayLoading from './BaseOverlayLoading.vue';
import BaseFieldInput from './BaseFieldInput.vue';
import BaseTable from './BaseTable.vue';
import BaseTableHead from './BaseTableHead.vue';
import BaseTableRow from './BaseTableRow.vue';
import BaseTableCellHeader from './BaseTableCellHeader.vue';
import IconMagnifyingGlass from '@/modules/your/components/icons/IconMagnifyingGlass.vue';

const { t } = useI18n();

interface Props {
    attributes: Attribute[];
    search?: string;
    sorting?: string;
    items: TableItem[];
    isLoading?: boolean;
    meta?: PaginationMeta;
    initialSorting?: string;
    validationError?: IndexValidationError | null;
    isSearchable?: boolean;
    isFilterable?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    search: '',
    sorting: '',
    initialSorting: '',
    isLoading: false,
    meta: undefined,
    validationError: null,
    isSearchable: false,
    isFilterable: false,
});

interface UpdatePageEventArgs extends PageInformation {
    sortings: string[];
    search: string;
}

const emits = defineEmits<{
    (e: 'update:page', updatePageEventArgs: UpdatePageEventArgs): void;
    (e: 'update:search', search: string): void;
    (e: 'update:sorting', sorting: string): void;
}>();

const searchableAttributeLabels = computed(() => {
    const labels = props.attributes
        .map((attribute) => {
            if (!attribute.isSearchable) {
                return null;
            }
            return attribute.label;
        })
        .filter((value) => value !== null)
        .join(', ');
    return labels;
});

function onUpdatePageSorting({ attributeName, sortDirection }) {
    if (props.meta) {
        emits('update:sorting', '');
        if (sortDirection) {
            emits('update:sorting', `${attributeName}:${sortDirection}`);
        }
        updatePage(1);
    }
}

function updatePage(page: number, pageSize?: number) {
    if (props.meta) {
        emits('update:page', {
            page,
            pageSize: pageSize ?? props.meta.per_page,
            sortings: [props.sorting],
            search: props.search,
        } as UpdatePageEventArgs);
    }
}

const onSearchClear = () => {
    emits('update:search', '');
    updatePage(1);
};
</script>
